import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Dialog } from "../../../../components/Dialogs/Dialog";
import { ISignature } from "../../../../redux/signature/signature.types";
import { IStoreState } from "../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../hooks";
import { fetchAllSignatures } from "../../../../redux/signature/signatureThunk";
import { LoadState } from "../../../../constants/enums";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Radio,
  Typography,
} from "@mui/material";
import { CSS_CONSTANTS } from "../../../../constants/cssConstants";

interface ISignaturePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onInsert: (htmlContent: string) => void;
}

export const SignaturePickerDialog: React.FC<ISignaturePickerDialogProps> = ({
  open,
  onClose,
  onInsert,
}) => {
  const dispatch = useDispatchWrapper();
  const { allSignatures, allSignaturesLoading } = useSelector(
    (storeState: IStoreState) => storeState.signature,
  );
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedCode(null);
      dispatch(fetchAllSignatures());
    }
  }, [open]);

  const handleAdd = () => {
    if (!selectedCode) return;
    const selected = allSignatures.find(
      (s: ISignature) => s.signature_code === selectedCode,
    );
    if (selected) {
      onInsert(selected.html_content);
    }
  };

  const loading = allSignaturesLoading === LoadState.InProgress;
  const failed = allSignaturesLoading === LoadState.Failed;
  const loaded = allSignaturesLoading === LoadState.Loaded;

  return (
    <Dialog
      open={open}
      title="Select Signature"
      size="md"
      fullWidth
      onClose={onClose}
      actions={[
        {
          label: "Cancel",
          type: "button",
          variant: "text",
          onClick: onClose,
        },
        {
          label: "Add",
          type: "button",
          variant: "contained",
          disabled: !selectedCode,
          onClick: handleAdd,
        },
      ]}
    >
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {failed && (
        <Typography color="error" py={2}>
          Failed to load signatures
        </Typography>
      )}

      {loaded && allSignatures.length === 0 && (
        <Typography color="textSecondary" py={2}>
          No signatures available
        </Typography>
      )}

      {loaded && allSignatures.length > 0 && (
        <Grid container spacing={2}>
          {allSignatures.map((sig: ISignature) => (
            <Grid item xs={12} sm={6} key={sig.signature_code}>
              <Paper
                variant="outlined"
                onClick={() => setSelectedCode(sig.signature_code)}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  borderColor:
                    selectedCode === sig.signature_code
                      ? "primary.main"
                      : "divider",
                  borderWidth: selectedCode === sig.signature_code ? 2 : 1,
                  transition: "border-color 0.2s",
                  "&:hover": {
                    borderColor: "primary.light",
                  },
                }}
              >
                <Box display="flex" alignItems="center" mb={1}>
                  <Radio
                    checked={selectedCode === sig.signature_code}
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                  />
                  <Typography variant="body1" sx={{fontSize: CSS_CONSTANTS.LABEL_HEADING_SIZE}} fontWeight={600} noWrap>
                    {sig.signature_name}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: 120,
                    overflow: "hidden",
                    borderRadius: 1,
                    bgcolor: "grey.50",
                    p: 1,
                    fontSize: "11px",
                    "& img": { maxWidth: "100%", height: "auto" },
                  }}
                  dangerouslySetInnerHTML={{ __html: sig.html_content }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Dialog>
  );
};
